import type { BuyRequest, Product } from '@/js/services/RogueTypes'

const KG_PER_LB = 0.45359237

// Catalog values are free text ("5.5", "22LB", "20LB (pair), rated to 1000+LB") — only an unambiguous single number counts.
const WEIGHT_PATTERN = /^\s*(\d+(?:\.\d+)?)\s*(?:lbs?)?\s*$/i

export function parseWeightLb(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value !== 'string') return undefined

  const match = WEIGHT_PATTERN.exec(value)

  return match ? Number(match[1]) : undefined
}

// Weight of one product: configurable = chosen child, bundle = qty > 0 options × option qty, grouped = qty > 0 rows once; bad weights count as 0.
export function getBuyRequestWeightLb(
  buyRequest: BuyRequest,
  getProduct: (productId: number) => Product | undefined
): { lb: number, invalidProductIds: number[] } {
  const invalidProductIds: number[] = []

  const unitWeight = (br: BuyRequest): number => {
    const product = getProduct(br.productId)
    const children = br.children || []

    if (product?.type === 'configurable') {
      return children[0] ? unitWeight(children[0]) : 0
    }

    if (children.length) {
      // A grouped row's qty is the customer's purchase quantity, which the total ignores; a bundle option's qty is part of the configuration.
      const countsQty = product?.type === 'bundle'

      return children
        .filter(child => Boolean(child) && child.qty > 0)
        .reduce((acc, child) => acc + (countsQty ? child.qty : 1) * unitWeight(child), 0)
    }

    const weight = parseWeightLb(product?.attributes?.adlinfo_weight)
    if (weight === undefined) {
      invalidProductIds.push(br.productId)

      return 0
    }

    return weight
  }

  return { lb: unitWeight(buyRequest), invalidProductIds }
}

export function isTotalWeightEnabled(
  buyRequest: BuyRequest,
  getProduct: (productId: number) => Product | undefined
): boolean {
  // Magento sets the flag on the bundle / configurable level, not the grouped PDP, so any selected product counts.
  return getProduct(buyRequest.productId)?.attributes?.show_total_weight === true
    || (buyRequest.children || []).some(child => Boolean(child) && child.qty > 0 && isTotalWeightEnabled(child, getProduct))
}

export function formatWeight(lb: number): { lb: string, kg: string } {
  return {
    lb : String(Math.round(lb * 10) / 10),
    kg : (Math.round(lb * KG_PER_LB * 10) / 10).toFixed(1),
  }
}
